import {
    moduleNavTree,
    type BusinessModuleId,
    type ModuleNavLeaf,
} from "./moduleNavigation";

export function parseNavHref(href: string) {
    const [pathname, query = ""] = href.split("?");
    const search = query ? `?${query}` : "";

    return { pathname, search };
}

export function navHrefBasePath(href: string) {
    return parseNavHref(href).pathname;
}

export function isSelectedNavHref(pathname: string, search: string, href: string) {
    const target = parseNavHref(href);

    if (pathname !== target.pathname && !pathname.startsWith(`${target.pathname}/`)) {
        return false;
    }

    if (!target.search) {
        return true;
    }

    const normalizedSearch = search.startsWith("?") ? search : search ? `?${search}` : "";
    const currentParams = new URLSearchParams(normalizedSearch.startsWith("?") ? normalizedSearch.slice(1) : normalizedSearch);
    const targetParams = new URLSearchParams(
        target.search.startsWith("?") ? target.search.slice(1) : target.search,
    );

    for (const [key, value] of targetParams.entries()) {
        if (currentParams.get(key) !== value) {
            return false;
        }
    }

    return true;
}

function collectNavLeaves(nodes: ModuleNavLeaf[]): ModuleNavLeaf[] {
    const leaves: ModuleNavLeaf[] = [];

    for (const node of nodes) {
        if (node.children?.length) {
            leaves.push(...collectNavLeaves(node.children));
        } else if (node.href) {
            leaves.push(node);
        }
    }

    return leaves;
}

export function findActiveNavLeaf(pathname: string, search: string): ModuleNavLeaf | null {
    const normalizedSearch = search.startsWith("?") ? search : search ? `?${search}` : "";
    const fullPath = `${pathname}${normalizedSearch}`;
    const leaves = collectNavLeaves(moduleNavTree.flatMap((group) => group.children));

    const exact = leaves.find((leaf) => leaf.href === fullPath);

    if (exact) {
        return exact;
    }

    const withQuery = leaves.filter((leaf) => {
        if (!leaf.href) {
            return false;
        }

        const target = parseNavHref(leaf.href);

        return target.search !== "" && isSelectedNavHref(pathname, normalizedSearch, leaf.href);
    });

    if (withQuery.length >= 1) {
        const ranked = [...withQuery].sort((left, right) => {
            const leftHref = left.href ?? "";
            const rightHref = right.href ?? "";
            const leftParams = new URLSearchParams(parseNavHref(leftHref).search.replace(/^\?/, ""));
            const rightParams = new URLSearchParams(parseNavHref(rightHref).search.replace(/^\?/, ""));

            return rightParams.toString().length - leftParams.toString().length;
        });

        return ranked[0] ?? null;
    }

    const pathMatches = leaves.filter((leaf) => {
        if (!leaf.href) {
            return false;
        }

        const target = parseNavHref(leaf.href);

        return (
            target.search === ""
            && (pathname === target.pathname || pathname.startsWith(`${target.pathname}/`))
        );
    });

    if (pathMatches.length === 1) {
        return pathMatches[0] ?? null;
    }

    if (pathMatches.length > 1) {
        const ranked = [...pathMatches].sort(
            (left, right) => (right.href?.length ?? 0) - (left.href?.length ?? 0),
        );

        return ranked[0] ?? null;
    }

    return null;
}

export function resolveActiveWorkspaceItem(pathname: string, search: string) {
    const leaf = findActiveNavLeaf(pathname, search);

    if (leaf) {
        return {
            path: pathname,
            label: leaf.label,
            description: leaf.description ?? "",
        };
    }

    return null;
}

function findModuleForLeafId(leafId: string): BusinessModuleId | null {
    const walk = (nodes: ModuleNavLeaf[], moduleId: BusinessModuleId): boolean => {
        for (const node of nodes) {
            if (node.id === leafId) {
                return true;
            }

            if (node.children?.length && walk(node.children, moduleId)) {
                return true;
            }
        }

        return false;
    };

    for (const group of moduleNavTree) {
        if (walk(group.children, group.id)) {
            return group.id;
        }
    }

    return null;
}

export function findActiveModuleId(pathname: string, search: string): BusinessModuleId | null {
    const leaf = findActiveNavLeaf(pathname, search);

    if (!leaf) {
        return null;
    }

    return findModuleForLeafId(leaf.id);
}

export function findActiveNavBranchIds(pathname: string, search: string): string[] {
    const leaf = findActiveNavLeaf(pathname, search);

    if (!leaf) {
        return [];
    }

    const branch: string[] = [];

    const walk = (nodes: ModuleNavLeaf[]): boolean => {
        for (const node of nodes) {
            branch.push(node.id);

            if (node.id === leaf.id) {
                return true;
            }

            if (node.children?.length && walk(node.children)) {
                return true;
            }

            branch.pop();
        }

        return false;
    };

    for (const group of moduleNavTree) {
        branch.length = 0;

        if (walk(group.children)) {
            return [...branch];
        }
    }

    return [];
}
